import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { ArrowLeft, Check, X, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const questions = [
  { id: 'repas_fixes', label: 'Ai-je respecté mes repas fixes ?' },
  { id: 'couverts', label: 'Ai-je posé mes couverts entre les bouchées ?' },
  { id: 'sans_ecran', label: 'Ai-je mangé sans écran ?' },
  { id: 'satiete', label: 'Ai-je écouté ma satiété ?' },
  { id: 'bienveillance', label: 'Ai-je été bienveillant(e) envers moi-même ?' }
];

export const ChecklistSoir = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [answers, setAnswers] = useState({});
  const [saved, setSaved] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const res = await axios.get(`${API_URL}/checklist/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(res.data);
    } catch (e) {}
  };

  const toggleAnswer = (id) => {
    if (saved) return;
    setAnswers(prev => {
      const current = prev[id];
      if (current === undefined) return { ...prev, [id]: true };
      if (current === true) return { ...prev, [id]: false };
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const allAnswered = Object.keys(answers).length === questions.length;

  const saveChecklist = async () => {
    try {
      await axios.post(`${API_URL}/checklist/save`, { answers }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSaved(true);
      toast.success('Checklist enregistrée');
      fetchHistory();
    } catch (e) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const reset = () => {
    setAnswers({});
    setSaved(false);
  };

  const yesCount = Object.values(answers).filter(v => v === true).length;

  const formatDate = (iso) => {
    return new Date(iso).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  return (
    <div className="min-h-screen bg-sand-50 pb-24" data-testid="checklist-soir-page">
      <div className="bg-gradient-to-b from-sage-100 to-sand-50 px-6 py-8">
        <Button variant="ghost" size="icon" onClick={() => navigate('/outils')} className="mb-4">
          <ArrowLeft className="w-5 h-5 text-sage-700" />
        </Button>
        <h1 className="text-3xl font-serif font-light text-sage-700 mb-2">
          Checklist du soir
        </h1>
        <p className="text-sage-500 text-sm">5 questions pour faire le point sur votre journée</p>
      </div>

      <div className="px-6 py-6">
        <div className="max-w-md mx-auto space-y-3">
          {questions.map((q) => {
            const val = answers[q.id];
            return (
              <button
                key={q.id}
                onClick={() => toggleAnswer(q.id)}
                className={`w-full flex items-center gap-4 p-5 rounded-xl border-2 transition-all text-left ${
                  val === true
                    ? 'border-green-400 bg-green-50'
                    : val === false
                    ? 'border-red-300 bg-red-50/50'
                    : 'border-sage-200 bg-white hover:border-sage-300'
                }`}
                data-testid={`checklist-${q.id}`}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                  val === true
                    ? 'bg-green-500'
                    : val === false
                    ? 'bg-red-400'
                    : 'bg-sage-100'
                }`}>
                  {val === true && <Check className="w-4 h-4 text-white" />}
                  {val === false && <X className="w-4 h-4 text-white" />}
                </div>
                <span className="text-sm text-sage-700 flex-1">{q.label}</span>
              </button>
            );
          })}

          {/* Résultat */}
          {allAnswered && !saved && (
            <div className="pt-4">
              <Button
                onClick={saveChecklist}
                className="w-full bg-sage-600 hover:bg-sage-600 text-white h-12 rounded-xl"
                data-testid="checklist-save"
              >
                Enregistrer ({yesCount}/{questions.length})
              </Button>
            </div>
          )}

          {saved && (
            <div className="pt-4 text-center space-y-4">
              <div className="bg-white rounded-xl p-5 border border-sage-100">
                <p className="text-3xl font-serif text-sage-700 mb-2">{yesCount}/{questions.length}</p>
                <p className="text-sage-600 text-sm leading-relaxed">
                  {yesCount === 5
                    ? (history.filter(h => Object.values(h.answers || {}).filter(v => v === true).length === 5).length >= 3
                      ? 'Plusieurs jours à 5/5 ! Les automatismes se mettent en place, continuez.'
                      : 'Journée parfaite ! Bravo.')
                    : yesCount >= 4
                    ? 'Très belle journée ! Vous êtes sur la bonne voie. Le progrès n\'a pas besoin d\'être parfait.'
                    : yesCount >= 3
                    ? 'Belle journée ! Concentrez-vous sur un seul point à améliorer demain.'
                    : yesCount >= 1
                    ? 'Chaque "oui" compte. Même un seul, c\'est un pas.'
                    : 'Journée difficile, et c\'est normal. La bienveillance envers vous-même est le premier pas.'
                  }
                </p>
              </div>
              
              {/* Conseil personnalisé */}
              {answers.couverts === false && (
                <div className="bg-sage-50 rounded-xl p-4 border border-sage-100 text-left">
                  <p className="text-sage-600 text-sm">Astuce : essayez le "Guide sonore" pour vous aider à ralentir.</p>
                </div>
              )}
              {answers.sans_ecran === false && answers.couverts !== false && (
                <div className="bg-sage-50 rounded-xl p-4 border border-sage-100 text-left">
                  <p className="text-sage-600 text-sm">Astuce : commencez par un seul repas sans écran. Le petit-déjeuner est souvent le plus facile.</p>
                </div>
              )}
              {answers.satiete === false && answers.couverts !== false && answers.sans_ecran !== false && (
                <div className="bg-sage-50 rounded-xl p-4 border border-sage-100 text-left">
                  <p className="text-sage-600 text-sm">Astuce : faites une pause au milieu du repas. Posez les couverts et demandez-vous "ai-je encore faim ?"</p>
                </div>
              )}
              {answers.bienveillance === false && yesCount <= 2 && (
                <div className="bg-sage-50 rounded-xl p-4 border border-sage-100 text-left">
                  <p className="text-sage-600 text-sm">Rappel : un écart n'est pas un échec. C'est la culpabilité qui fait plus de dégâts que l'écart lui-même.</p>
                </div>
              )}

              <Button
                onClick={reset}
                variant="outline"
                className="w-full border-sage-200 text-sage-600 h-11 rounded-xl text-sm"
              >
                Recommencer
              </Button>
            </div>
          )}

          {/* Historique */}
          {history.length > 0 && (
            <div className="pt-4">
              <button
                onClick={() => setShowHistory(!showHistory)}
                className="flex items-center gap-2 text-sage-500 text-sm w-full justify-center py-2"
              >
                Mon historique
                {showHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              {showHistory && (
                <div className="space-y-2 mt-2">
                  {history.map((entry, i) => {
                    const yes = Object.values(entry.answers).filter(v => v === true).length;
                    const total = Object.keys(entry.answers).length;
                    return (
                      <div key={entry.id || i} className="flex items-center justify-between bg-white rounded-lg p-3 border border-sage-100">
                        <span className="text-xs text-sage-500">{formatDate(entry.created_at)}</span>
                        <div className="flex items-center gap-2">
                          <div className="flex gap-0.5">
                            {questions.map((q) => (
                              <div
                                key={q.id}
                                className={`w-3 h-3 rounded-full ${
                                  entry.answers[q.id] === true ? 'bg-green-400' : entry.answers[q.id] === false ? 'bg-red-300' : 'bg-sage-200'
                                }`}
                              />
                            ))}
                          </div>
                          <span className="text-sm font-medium text-sage-700">{yes}/{total}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChecklistSoir;
